import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import SupportButton from '@atoms/SupportButton/SupportButton';
import { IBreadcrumb } from '@models/IBreadcrumb';
import { IconName } from '@models/IIcon';
import { IImage } from '@models/IImage';
import ILanguageSelector from '@models/ILanguageSelector';
import { ILink } from '@models/ILink';
import IListOfLinks from '@models/IListOfLinks';
import { IPaymentMethods } from '@models/IPaymentMethods';
import { IStoreLocatorPlace } from '@models/IStoreLocatorAutocomplete';
import { IStoreLocatorSearch } from '@models/IStoreLocatorSearch';
import FooterBanner from '@molecules/FooterBanner/FooterBanner';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import StoreLocatorAutosuggest from '@molecules/StoreLocatorAutosuggest/StoreLocatorAutosuggest';
import Breadcrumbs from '@organisms/Breadcrumb/Breadcrumb';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import updateDataLayer from '@utils/helpers/gtm';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import qs from 'qs';
import { useEffect, useState } from 'react';
import storeLocatorAutocomplete from '../../../services/storeLocatorAutocomplete';
import storeLocatorSearch from '../../../services/storeLocatorSearch';
import LinksSection from './LinksSection';

interface Props {
  readonly backToTopButtonText?: string;
  readonly breadcrumb: IBreadcrumb;
  readonly copyrightNotice: string;
  readonly enableFindAStore: boolean;
  readonly enableStickyBackToTopButton: boolean;
  readonly externalSitesLinks: IListOfLinks;
  readonly findAStoreAutocompleteEndpoint: string;
  readonly findAStoreButtonText: string;
  readonly findAStoreCountryCode: string;
  readonly findAStoreHeader: string;
  readonly findAStorePlaceholder: string;
  readonly findAStoreRedirectUrl: string;
  readonly findAStoreSearchEndpoint: string;
  readonly findAStoreSubHeader: string;
  readonly footerLinks?: IListOfLinks[];
  readonly homepageLink?: ILink;
  readonly language: string;
  readonly languageSelector?: ILanguageSelector;
  readonly logo: IImage;
  readonly campaignLogo: IImage;
  readonly secondaryNavigationLinks: ILink[];
  readonly socialLinks: ILink[];
  readonly supportButtonLink: ILink;
  readonly enableSupportButton: boolean;
  readonly paymentMethods?: IPaymentMethods;
  readonly cookieSettingsLabel?: string;
}

const Footer = ({
  findAStoreAutocompleteEndpoint,
  findAStoreButtonText,
  findAStoreCountryCode,
  findAStoreHeader,
  findAStorePlaceholder,
  findAStoreRedirectUrl,
  findAStoreSearchEndpoint,
  findAStoreSubHeader,
  backToTopButtonText,
  breadcrumb,
  copyrightNotice,
  enableFindAStore,
  enableStickyBackToTopButton,
  externalSitesLinks,
  footerLinks = [],
  homepageLink,
  language,
  languageSelector,
  logo,
  campaignLogo,
  secondaryNavigationLinks,
  socialLinks,
  supportButtonLink,
  enableSupportButton,
  paymentMethods,
  cookieSettingsLabel,
}: Props) => {
  const [storeQueryText, setStoreQueryText] = useState<string>('');
  const [place, setPlace] = useState<IStoreLocatorPlace | null>(null);
  const [paddingBottom, setPaddingBottom] = useState(0);
  const [openLinksSectionTitle, setOpenLinksSectionTitle] = useState('');
  const isInIFrame = useIsInIFrame();
  const [footerBannerRef, { clientHeight: footerBannerHeight }] =
    useResizeObserver();
  const [paymentMethodsRef, { clientHeight: paymentMethodsHeight }] =
    useResizeObserver();
  const { stickyBuyBarHeight, tabbedProductNavigationBottomHeight } =
    useComponentsHeightsStore((state) => ({
      stickyBuyBarHeight: state.stickyBuyBarHeight,
      tabbedProductNavigationBottomHeight:
        state.tabbedProductNavigationBottomHeight,
    }));

  useEffect(() => {
    setPaddingBottom(
      Math.max(stickyBuyBarHeight, tabbedProductNavigationBottomHeight, 0)
    );
  }, [stickyBuyBarHeight, tabbedProductNavigationBottomHeight]);

  const { url: homepageUrl = '', target: homepageTarget = '_self' } =
    homepageLink || {};

  const getFirstAutocompletePlace = async (searchTerm: string) => {
    try {
      const autoCompleteResponse = await storeLocatorAutocomplete({
        endpoint: findAStoreAutocompleteEndpoint,
        countryCode: findAStoreCountryCode,
        searchTerm,
      });
      const firstSuggestion = autoCompleteResponse[0];
      const newPlace = firstSuggestion
        ? {
            id: firstSuggestion.placeId,
            name: firstSuggestion.description,
          }
        : null;

      return newPlace;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });
      return null;
    }
  };

  const getSearchedPlace = async (
    currentPlace: IStoreLocatorPlace | null,
    currentQueryText: string
  ) => {
    if (currentPlace) return currentPlace;
    if (!currentQueryText) return null;

    const firstSuggestedPlace =
      await getFirstAutocompletePlace(currentQueryText);
    return firstSuggestedPlace;
  };

  const handleStoresSearch = async () => {
    try {
      const searchedPlace = await getSearchedPlace(place, storeQueryText);
      const { latitude, longitude } = searchedPlace
        ? await storeLocatorSearch({
            endpoint: findAStoreSearchEndpoint,
            countryCode: findAStoreCountryCode,
            languageCode: language,
            placeId: searchedPlace.id,
          })
        : ({} as IStoreLocatorSearch);

      updateDataLayer({
        event: 'storeFinder',
        search_term: storeQueryText,
      });

      const newQueryText = searchedPlace?.name || storeQueryText;
      const newQueryParams = {
        distance: 50,
        ...(newQueryText ? { query: newQueryText } : {}),
        ...(parseFloat(latitude) ? { latitude } : {}),
        ...(parseFloat(longitude) ? { longitude } : {}),
      };
      const newQueryString = qs.stringify(newQueryParams, {
        addQueryPrefix: true,
      });

      window.location.href = `${findAStoreRedirectUrl}${newQueryString}`;
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
      console.log({ error });
    }
  };

  const handleSocialClick = (platform: string) => {
    updateDataLayer({
      event: 'socialClick',
      platform,
    });
  };

  return (
    <footer
      className="dark bg-black-100 text-white"
      data-testid="CT-02-Footer"
      style={{
        paddingBottom: `${paddingBottom}px`,
      }}
    >
      <div className="relative z-10 bg-grey-600 pt-3 md:pb-28 xl:pt-10">
        <SoftLoadWrapper>
          <div className="container flex py-0.5">
            <div className="flex flex-row items-center">
              {logo?.desktop?.url && (
                <a
                  href={homepageUrl}
                  target={isInIFrame ? '_parent' : homepageTarget}
                  className="mr-5 shrink-0 xl:mr-8"
                >
                  <Image
                    image={logo}
                    imageClasses="h-10 w-auto"
                    dataTestId="CT-02-Footer-Logo"
                  />
                </a>
              )}
              {campaignLogo?.desktop?.url && (
                <Image
                  image={campaignLogo}
                  imageClasses="md:h-[21px] h-[19px] w-auto"
                />
              )}
            </div>

            <div className="ml-auto hidden md:block">
              <ul className="flex">
                {socialLinks?.map((link) => (
                  <li
                    className="ml-5 first:ml-0"
                    key={`social-links-${link.url}`}
                  >
                    <a
                      href={link.url}
                      onClick={() => handleSocialClick(`${link.name}`)}
                      target={isInIFrame ? '_parent' : '_blank'}
                      rel="noreferrer"
                      aria-label={link.name}
                      data-testid={`CT-02-Footer-Social-Icon-${link.name}`}
                    >
                      {link.linkIcon && (
                        <Icon
                          name={link.linkIcon}
                          className="text-white transition duration-300 ease-in-out hover:text-yellow"
                        />
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {breadcrumb && (
            <div className="container pt-[58px] pb-3">
              <Breadcrumbs
                activePage={breadcrumb.activePage}
                links={breadcrumb.links}
                isInFooter
              />
            </div>
          )}

          <div
            className={classNames(
              'container grid py-12 max-md:gap-7.5 md:grid-cols-2 md:gap-4',
              enableFindAStore ? 'xl:grid-cols-6' : 'xl:grid-cols-5'
            )}
          >
            {footerLinks?.length > 0 &&
              footerLinks.map((links) => (
                <LinksSection
                  {...links}
                  key={`footer-links-${links.title}`}
                  isOpen={openLinksSectionTitle === links.title}
                  onSetOpenTitle={setOpenLinksSectionTitle}
                />
              ))}

            {enableFindAStore && (
              <div className="col-span-full mt-12 md:mt-0 md:w-1/2 xl:col-start-6 xl:ml-auto xl:w-full">
                {findAStoreHeader && (
                  <p className="body-two mb-5 text-grey-500">
                    {findAStoreHeader}
                  </p>
                )}

                <div>
                  <p className="mb-[5px]">{findAStoreSubHeader}</p>

                  <StoreLocatorAutosuggest
                    autocompleteEndpoint={findAStoreAutocompleteEndpoint}
                    className="rounded-[5px] bg-white"
                    countryCode={findAStoreCountryCode}
                    dataTestId="CT-02-Footer-Store-Locator-Autosuggest-Field"
                    queryText={storeQueryText}
                    searchPlaceholderLabel={findAStorePlaceholder}
                    type="footer"
                    onSetPlace={setPlace}
                    onSetQueryText={setStoreQueryText}
                    onSearch={handleStoresSearch}
                  />
                </div>

                <div className="mt-2.5">
                  <Button onClick={handleStoresSearch}>
                    {findAStoreButtonText}
                  </Button>
                </div>
              </div>
            )}

            <div className="mt-12 md:hidden">
              <ul className="flex justify-between">
                {socialLinks
                  .filter((link) => link.url && link.linkIcon)
                  .map(({ name = '', url = '', linkIcon }) => (
                    <li
                      className="ml-5 first:ml-0"
                      key={`social-links-mobile-${url}`}
                    >
                      <a
                        href={url}
                        target={isInIFrame ? '_parent' : '_blank'}
                        rel="noreferrer"
                        aria-label={name}
                        data-testid={`CT-02-Footer-Social-Icon-${name}`}
                      >
                        <Icon
                          name={linkIcon as IconName}
                          className="text-white transition duration-300 ease-in-out hover:text-yellow"
                        />
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </SoftLoadWrapper>
      </div>

      {!!paymentMethods?.items?.length && (
        <div
          ref={paymentMethodsRef}
          className="border-t-[0.0625rem] border-t-white/30 bg-grey-600"
          data-testid="CT-02-Footer-Payment-Methods"
        >
          <div className="container flex flex-wrap items-center justify-center gap-x-[2.375rem] gap-y-5 py-[1.5625rem] max-md:px-[1.1875rem] md:justify-start md:gap-x-[0.9375rem] md:py-[1.4375rem]">
            {paymentMethods.items.map((paymentMethod) => (
              <div
                key={paymentMethod.title}
                className="h-[1.5625rem] w-[2.3125rem] md:h-[1.375rem] md:w-[2.0625rem]"
              >
                <Image
                  image={paymentMethod.image}
                  imageClasses="h-full object-contain rounded-sm"
                  dataTestId="CT-02-Footer-Payment-Methods"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div ref={footerBannerRef}>
        <FooterBanner
          backToTopButtonText={backToTopButtonText}
          copyrightNotice={copyrightNotice}
          enableStickyBackToTopButton={enableStickyBackToTopButton}
          externalSitesLinks={externalSitesLinks}
          language={language}
          languageSelector={languageSelector}
          secondaryNavigationLinks={secondaryNavigationLinks}
          cookieSettingsLabel={cookieSettingsLabel}
        />
      </div>

      {enableSupportButton &&
        supportButtonLink?.url &&
        supportButtonLink?.name &&
        !isInIFrame && (
          <SupportButton
            link={supportButtonLink}
            desktopBottomLimit={footerBannerHeight + paymentMethodsHeight}
          />
        )}
    </footer>
  );
};

export default Footer;
